// Composable para conversión de fechas y tiempos
// Extrae funcionalidad de conversión de useBaseModule

import moment from "moment";

/**
 * Interfaz para representar fechas simples (año, mes, día)
 */
export interface SimpleDate {
  year: number;
  month: number;
  day: number;
}

/**
 * Interfaz para representar tiempos simples (horas, minutos)
 */
export interface SimpleTime {
  hours: number;
  minutes: number;
}

/**
 * Composable para convertir entre diferentes formatos de fecha y tiempo
 */
export function useDateConverter() {
  /**
   * Convierte un objeto SimpleDate a Date nativo de JavaScript
   * @param date - Objeto con year, month, day
   * @returns Date con hora fijada a las 12:00:00 o null si inválido
   */
  function convertSimpleDateToDate(date: SimpleDate | null): Date | null {
    if (!date || !date.year || !date.month || !date.day) {
      return null;
    }
    // Mes - 1 porque JavaScript usa 0-11 para meses
    return new Date(date.year, date.month - 1, date.day, 12, 0, 0);
  }

  /**
   * Convierte un Date nativo de JavaScript a SimpleDate
   * @param date - Fecha nativa
   * @returns Objeto SimpleDate o null si inválido
   */
  function convertDateToSimpleDate(date: Date | null): SimpleDate | null {
    if (!date) {
      return null;
    }
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1, // Mes + 1 porque JavaScript usa 0-11
      day: date.getDate(),
    };
  }

  /**
   * Convierte un objeto SimpleTime a formato HH:mm
   * @param time - Objeto con hours y minutes
   * @returns String en formato "HH:mm" o null si inválido
   */
  function convertSimpleTimeToHHMM(time: SimpleTime | null): string | null {
    if (!time || time.hours === undefined || time.minutes === undefined) {
      return null;
    }

    const date = new Date();
    date.setHours(time.hours);
    date.setMinutes(time.minutes);

    return moment(date).format("HH:mm");
  }

  /**
   * Convierte un número decimal (horas) a formato HH:mm
   * Ejemplo: 1.5 -> "1:30", -2.25 -> "-2:15"
   * @param value - Horas en formato decimal
   * @returns String en formato "H:mm" con signo si es negativo
   */
  function convertDoubleToHHMM(value: number | null): string {
    if (value === null) {
      return "0:00";
    }

    const sign = value < 0 ? "-" : "";

    // Pasamos horas a minutos y redondeamos (evita fallos de redondeo)
    let minutes = Math.round(Math.abs(value) * 60);
    const hours = Math.floor(minutes / 60);
    minutes -= hours * 60;

    return `${sign}${hours}:${minutes.toString().padStart(2, "0")}`;
  }

  /**
   * Compara dos objetos SimpleDate
   * @param date1 - Primera fecha
   * @param date2 - Segunda fecha
   * @returns -1 si date1 < date2, 0 si iguales, 1 si date1 > date2
   */
  function compareSimpleDates(
    date1: SimpleDate | null,
    date2: SimpleDate | null
  ): number {
    if (date1 === null && date2 === null) {
      return 0;
    }
    if (date1 !== null && date2 === null) {
      return -1;
    }
    if (date1 === null && date2 !== null) {
      return 1;
    }

    const dt1 = convertSimpleDateToDate(date1);
    const dt2 = convertSimpleDateToDate(date2);

    if (!dt1 || !dt2) return 0;

    return dt1.getTime() - dt2.getTime();
  }

  return {
    convertSimpleDateToDate,
    convertDateToSimpleDate,
    convertSimpleTimeToHHMM,
    convertDoubleToHHMM,
    compareSimpleDates,
  };
}
